import { useQueryClient } from "@tanstack/react-query";
import { Checkbox, Input, Typography } from "antd";
import { useStoreCustomDomainMutation, useUpdateCustomDomainMutation } from "api/TenantSlug/CustomDomain/hooks";
import { labelsApi } from "api/TenantSlug/Label/api";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { MULTISELECT_ALL_VALUE } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { LabelType } from "types/enums";
import { FormId } from "types/models";

import { ManageCustomDomainFormData, schema } from "./formSchema";

import CustomDomainData = App.Data.CustomDomainData;

interface ManageCustomDomainFormProps {
    onSubmit?: () => void;
    domain?: CustomDomainData;
}

const ManageCustomDomainFormContent: FC<ManageCustomDomainFormProps> = () => {
    const { t } = usePaldockTranslation("forms.manage-custom-domain");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ManageCustomDomainFormData>();
    const { control, fieldNames } = generalFormUtils;

    return (
        <div>
            <div className="grid gap-x-4 grid-cols-3 p-4 col-span-3 bg-white m-3 rounded-lg">
                <div className="col-span-3 my-3">
                    <Typography.Title level={4}>{tC("general")}</Typography.Title>
                </div>
                <div className="grid grid-cols-2 gap-x-4 col-span-3">
                    <FormItem control={control} name={fieldNames.name.name} label={tFields("name.label")} required>
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.dnsDomain?.name || "dnsDomain"}
                        label={t("fields.dns_domain.label")}
                    >
                        <Input placeholder={t("fields.dns_domain.placeholder")} readOnly />
                    </FormItem>
                </div>
            </div>

            <div className="grid gap-x-4 grid-cols-3 p-4 col-span-3 bg-white m-3 rounded-lg">
                <div className="col-span-3 my-3">
                    <Typography.Title level={4}>{tC("settings")}</Typography.Title>
                </div>

                <div className="col-span-3">
                    <FormItem control={control} name={fieldNames.use_for_offers?.name || "use_for_offers"}>
                        <Checkbox>{t("fields.use_for_offers.label")}</Checkbox>
                    </FormItem>
                </div>

                <div className="grid grid-cols-2 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.country_id?.name || "country_id"}
                        label={tFields("country.label")}
                    >
                        <CountrySelectSearchable allowClear />
                    </FormItem>

                    <FormItem
                        control={control}
                        name={fieldNames.default_category_ids?.name || "default_category_ids"}
                        label={t("fields.default_categories.label")}
                        required
                    >
                        <CategorySelectSearchable mode={"multiple"} allowClear />
                    </FormItem>
                </div>

                <div className="col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.include_category_ids?.name || "include_category_ids"}
                        label={t("fields.included_categories.label")}
                        required
                    >
                        <CategorySelectSearchable mode={"multiple"} className="w-100" />
                    </FormItem>
                </div>

                <div className="col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.exclude_category_ids?.name || "exclude_category_ids"}
                        label={t("fields.excluded_categories.label")}
                        required
                    >
                        <CategorySelectSearchable mode={"multiple"} className="w-100" />
                    </FormItem>
                </div>
            </div>
        </div>
    );
};

export const ManageCustomDomainForm: FC<ManageCustomDomainFormProps> = ({ onSubmit, domain }) => {
    const dnsUrl = import.meta.env.VITE_DNS_DOMAIN_URL || "domains.paldev.cz";
    const { t } = usePaldockTranslation("forms.manage-custom-domain");

    if (!import.meta.env.VITE_DNS_DOMAIN_URL) {
        console.warn("VITE_DNS_DOMAIN_URL environment variable is not set, using fallback value");
    }

    const formId = FormId.ManageCustomDomainForm;
    const isEditMode = Boolean(domain);

    const defaultValues: ManageCustomDomainFormData = {
        name: domain?.name || "",
        dnsDomain: dnsUrl,
        use_for_offers: domain?.use_for_offers ?? false,
        country_id: domain?.country?.id ?? domain?.country_id ?? null,
        default_category_ids: domain?.defaultCategories?.map((category: any) => category.id.toString()) || [],
        exclude_category_ids: domain?.excludeCategories?.map((category: any) => category.id.toString()) || [],
        include_category_ids: domain?.includeCategories?.map((category: any) => category.id.toString()) || [],
    };

    const queryClient = useQueryClient();
    const storeCustomDomainMutation = useStoreCustomDomainMutation();
    const updateCustomDomainMutation = useUpdateCustomDomainMutation();
    const generalFormUtils = useGeneralForm<ManageCustomDomainFormData>();

    const allCategories = useFetchAllPaginatedData(
        (payload) => labelsApi.index(payload),
        {
            filter: { filter: { type: LabelType.Category } },
        },
        "labels"
    );

    const handleFormSubmit = (values: ManageCustomDomainFormData) => {
        const replaceAllValue = (selected: string[] | undefined, allIds: number[]) => {
            if (!selected || selected.length === 0) return [];
            if (selected.includes(MULTISELECT_ALL_VALUE)) {
                return allIds;
            }
            return selected.map((id) => Number(id));
        };

        const defaultCategoryIds = replaceAllValue(
            values.default_category_ids,
            allCategories.data.map((c) => Number(c.id))
        );

        const excludeCategoryIds = replaceAllValue(
            values.exclude_category_ids,
            allCategories.data.map((c) => Number(c.id))
        );

        const includeCategoryIds = replaceAllValue(
            values.include_category_ids,
            allCategories.data.map((c) => Number(c.id))
        );

        const payload = {
            name: values.name,
            use_for_offers: values.use_for_offers,
            country_id: values.country_id ?? null,
            default_category_ids: defaultCategoryIds,
            exclude_category_ids: excludeCategoryIds,
            include_category_ids: includeCategoryIds,
        };

        const successMessage = isEditMode ? t("messages.update_success") : t("messages.add_success");
        const errorMessage = isEditMode ? t("messages.update_error") : t("messages.add_error");

        if (isEditMode) {
            updateCustomDomainMutation.mutate(
                { domainId: domain!.id, request: payload },
                {
                    onSuccess: () => {
                        // Invalidate both the specific domain query and the domains list
                        queryClient.invalidateQueries({ queryKey: ["getCustomDomain", domain!.id] });
                        queryClient.invalidateQueries({ queryKey: ["getCustomDomains"] });
                        toast.success(successMessage);
                        onSubmit?.();
                    },
                    onError: (error: any) => {
                        toast.error(errorMessage);
                        generalFormUtils?.handleErrorsFromErrorResponse(error);
                    },
                }
            );
        } else {
            storeCustomDomainMutation.mutate(payload, {
                onSuccess: () => {
                    // Invalidate the domains list for new domain
                    queryClient.invalidateQueries({ queryKey: ["getCustomDomains"] });
                    toast.success(successMessage);
                    onSubmit?.();
                },
                onError: (error: any) => {
                    toast.error(errorMessage);
                    generalFormUtils?.handleErrorsFromErrorResponse(error);
                },
            });
        }
    };

    return (
        <GeneralFormProvider<ManageCustomDomainFormData>
            key={domain?.id || "create"}
            id={formId}
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={handleFormSubmit}
        >
            <ManageCustomDomainFormContent onSubmit={onSubmit} />
        </GeneralFormProvider>
    );
};
